import React from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaClock, FaPhone, FaEnvelope, FaGlobeAmericas } from 'react-icons/fa';

const LocationFooter = () => {
    const locations = [
        {
            id: 1,
            country: "India",
            status: "active",
            image: "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80",
            details: {
                address: "Surat, Gujarat",
                hours: "Mon-Fri: 9AM - 6PM",
                phone: "+91 6356771660",
                email: "business@unitechnostack.com"
            }
        },
        {
            id: 2,
            country: "Australia",
            status: "active",
            image: "https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80",
            details: {
                address: "Melbourne",
                hours: "Mon-Fri: 9AM - 6PM",
                phone: "+61 450 290 914",
                email: "business@unitechnostack.com"
            }
        },
        {
            id: 3,
            country: "USA",
            status: "coming",
            image: "https://images.unsplash.com/photo-1485871981521-5b1fd3805eee?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80",
            details: {
                address: "Coming soon to New York",
                hours: "Opening 2026",
                phone: "Contact for details",
                email: "business@unitechnostack.com"
            }
        },
        {
            id: 4,
            country: "Europe",
            status: "coming",
            image: "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80",
            details: {
                address: "Coming soon to London",
                hours: "Opening 2026",
                phone: "Contact for details",
                email: "business@unitechnostack.com"
            }
        }
    ];

    return (
        <div className=" text-white py-1 px-4 sm:px-6 lg:px-8">
            <div className="max-w-9xl mx-auto ">
                <div className="text-center mb-16 ">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                        className="inline-flex items-center justify-center mb-4"
                    >
                        <div className="bg-blue-900/30 p-3 rounded-full">
                            <FaGlobeAmericas className="text-blue-400 text-2xl" />
                        </div>
                    </motion.div>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        viewport={{ once: true }}
                        className="text-4xl md:text-5xl font-bold mb-4"
                    >
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-400">
                            Global Presence
                        </span>
                    </motion.h2>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        viewport={{ once: true }}
                        className="text-gray-400 max-w-2xl mx-auto text-lg"
                    >
                        Connecting with clients worldwide through our strategically located offices
                    </motion.p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {locations.map((location) => (
                        <motion.div
                            key={location.id}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: location.id * 0.1 }}
                            viewport={{ once: true }}
                            whileHover={{ y: -10 }}
                            className={`relative group overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 
                                ${location.status === "active"
                                    ? "border border-blue-500/30"
                                    : "border border-gray-700"}`}
                        >
                            {/* Location Image with Gradient Overlay */}
                            <div className="h-52 overflow-hidden relative">
                                <img
                                    src={location.image}
                                    alt={location.country}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

                                {/* Country Name */}
                                <div className="absolute bottom-4 left-4">
                                    <h3 className="text-2xl font-bold text-white">{location.country}</h3>
                                </div>
                            </div>

                            {/* Status Ribbon */}
                            <div className={`absolute top-0 right-0 px-4 py-2 rounded-bl-lg text-sm font-bold 
                                ${location.status === "active"
                                    ? "bg-gradient-to-r from-blue-600 to-cyan-600"
                                    : "bg-gradient-to-r from-purple-600 to-indigo-600"}`}>
                                {location.status === "active" ? "Active" : "Coming Soon"}
                            </div>

                            {/* Location Details */}
                            <div className="p-6 h-full bg-gradient-to-b from-gray-800 to-gray-900">
                                <div className="space-y-4">
                                    <div className="flex items-start">
                                        <FaMapMarkerAlt className="text-blue-400 mt-1 mr-3 flex-shrink-0" />
                                        <span className="text-gray-300">{location.details.address}</span>
                                    </div>
                                    <div className="flex items-center">
                                        <FaClock className="text-blue-400 mr-3 flex-shrink-0" />
                                        <span className="text-gray-300">{location.details.hours}</span>
                                    </div>
                                    <div className="flex items-center">
                                        <FaPhone className="text-blue-400 mr-3 flex-shrink-0" />
                                        <span className="text-gray-300">{location.details.phone}</span>
                                    </div>
                                    <div className="flex items-center">
                                        <FaEnvelope className="text-blue-400 mr-3 flex-shrink-0" />
                                        <span className="text-gray-300">{location.details.email}</span>
                                    </div>
                                </div>

                                {/* Contact Button */}
                                {/* <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className={`mt-6 w-full py-2.5 rounded-lg font-medium transition-all
                                        ${location.status === "active"
                                            ? "bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                                            : "bg-gradient-to-r from-gray-700 to-gray-800 text-gray-400 cursor-not-allowed"}`}
                                    disabled={location.status !== "active"}
                                >
                                    {location.status === "active" ? "Contact Office" : "Notify Me"}
                                </motion.button> */}
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Contact CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    viewport={{ once: true }}
                    className="mt-20 text-center bg-gradient-to-r from-blue-900/30 to-cyan-900/30 rounded-2xl p-8 border border-gray-700 mb-10"
                >
                    <div className="max-w-3xl mx-auto">
                        <h3 className="text-3xl font-bold mb-4">Ready to work with us?</h3>
                        <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                            Our team is ready to help you achieve your goals, no matter where you&apos;re located.
                            Contact us today to start your journey.
                        </p>
                        <a href="/quickenquiry" className="inline-flex items-center justify-center">
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-medium py-3.5 px-10 rounded-full inline-flex items-center shadow-xl hover:shadow-2xl transition-all"
                            >
                                <FaEnvelope className="mr-3" />
                                Get in Touch
                            </motion.button></a>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default LocationFooter;